import React, { useState, useRef, useEffect } from 'react';
import { motion, PanInfo } from 'framer-motion';
import { useSwipeable } from 'react-swipeable';

interface Word {
  id: string;
  english_word: string;
  turkish_word: string;
  example_sentence: string;
  example_sentence_turkish: string;
  difficulty: number;
}

interface WordCardProps {
  word: Word;
  onSwipeLeft: () => void;
  onSwipeRight: () => void;
  isProcessing?: boolean;
  onToggleFavorite?: (wordId: string) => void;
  isFavorite?: boolean;
}

const WordCard: React.FC<WordCardProps> = ({ word, onSwipeLeft, onSwipeRight, isProcessing = false, onToggleFavorite, isFavorite }) => {
  const [showTranslation, setShowTranslation] = useState(false);
  const [titleFontSize, setTitleFontSize] = useState(80);
  const titleRef = useRef<HTMLDivElement>(null);

  // Web Speech API için
  const speakWord = () => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(word.english_word);
      utterance.lang = 'en-US';
      utterance.rate = 0.8; // Biraz yavaş
      utterance.pitch = 1;
      speechSynthesis.speak(utterance);
    } else {
      console.log('Speech synthesis not supported');
    }
  };

  // Dynamic font sizing
  useEffect(() => {
    if (!titleRef.current) return;

    const container = titleRef.current;
    const containerWidth = container.offsetWidth - 4; // Safety margin
    let fontSize = 80; // Başlangıç boyutunu küçülttük
    const minFontSize = 20; // Minimum boyutu küçülttük
    const maxFontSize = word.english_word.length > 15 ? 200 : 360; // Maximum boyutları küçülttük

    const testFontSize = (size: number) => {
      container.style.fontSize = `${size}px`;
      return container.scrollWidth <= containerWidth;
    };

    // Binary search for optimal font size
    let low = minFontSize;
    let high = maxFontSize;

    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      if (testFontSize(mid)) {
        fontSize = mid;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }

    setTitleFontSize(fontSize);
  }, [word.english_word]);

  const handleDragEnd = (event: any, info: PanInfo) => {
    const threshold = 100;
    if (info.offset.x > threshold) {
      onSwipeRight();
    } else if (info.offset.x < -threshold) {
      onSwipeLeft();
    }
  };

  const swipeHandlers = useSwipeable({
    onSwipedLeft: onSwipeLeft,
    onSwipedRight: onSwipeRight,
    preventScrollOnSwipe: true,
    trackMouse: true,
  });

  const highlightWordInSentence = (sentence: string, targetWord: string) => {
    const regex = new RegExp(`\\b${targetWord}\\b`, 'gi');
    return sentence.replace(regex, '<span class="text-red-500 font-bold">$&</span>');
  };

  return (
    <div className="w-full max-w-lg mx-auto h-screen flex items-start justify-center p-4 pt-10 md:pt-14 pb-0">
      <div className="relative w-full max-w-[30rem]">
        <motion.div
          key={word.id}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          drag="x"
          dragConstraints={{ left: -250, right: 250 }}
          dragElastic={0.2}
          dragMomentum={true}
          onDragEnd={handleDragEnd}
          whileHover={{ scale: 1.02 }}
          className="w-full max-w-[30rem] h-[54vh] bg-white rounded-3xl shadow-[0_20px_60px_rgba(0,0,0,0.08)] overflow-hidden cursor-pointer"
          style={{ touchAction: 'pan-y' }}
          onClick={() => setShowTranslation(!showTranslation)}
          {...swipeHandlers}
        >
          <div className="px-12 pt-10 pb-10 text-center h-full flex flex-col">
            {/* Header with speaker + favorite icon */}
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleFavorite && onToggleFavorite(word.id);
                }}
                onMouseDown={(e) => e.stopPropagation()}
                className={`w-12 h-12 flex items-center justify-center rounded-full transition-colors ${isFavorite ? 'bg-yellow-100 hover:bg-yellow-200' : 'bg-gray-100 hover:bg-gray-200'}`}
                aria-label="Favorilere ekle/kaldır"
              >
                <img
                  src="/favorite.png"
                  alt="Favori"
                  className={`w-1/2 h-1/2 object-contain ${isFavorite ? 'opacity-100' : 'opacity-60'}`}
                />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  speakWord();
                }}
                onMouseDown={(e) => e.stopPropagation()}
                className="p-2 rounded-full bg-blue-100 hover:bg-blue-200 transition-colors"
                aria-label="Kelimeyi seslendir"
              >
                <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.794L4.5 14H2a1 1 0 01-1-1V7a1 1 0 011-1h2.5l4.883-3.794a1 1 0 011.617.794zM12.293 7.293a1 1 0 011.414 0L15 8.586l1.293-1.293a1 1 0 111.414 1.414L16.414 10l1.293 1.293a1 1 0 01-1.414 1.414L15 11.414l-1.293 1.293a1 1 0 01-1.414-1.414L13.586 10l-1.293-1.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>

                        {/* İngilizce Kelime */}
            <div className="flex-1 flex flex-col items-center justify-center">
              <h1
                ref={titleRef}
                className="english-title font-extrabold text-gray-900 tracking-tight leading-tight mb-2"
                style={{ fontSize: `${titleFontSize}px`, lineHeight: 1.05, whiteSpace: 'nowrap' }}
              >
                {word.english_word.toUpperCase()}
              </h1>
              
              {/* Türkçe Çeviri (kelime için) */}
              {showTranslation && (
                <motion.p 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-xl md:text-2xl text-gray-500 font-medium"
                >
                  {word.turkish_word}
                </motion.p>
              )}
            </div>

            {/* Örnek Cümle */}
            <div className="mt-auto">
              <p 
                className="text-base md:text-lg lg:text-xl text-gray-400 italic leading-relaxed"
                dangerouslySetInnerHTML={{
                  __html: highlightWordInSentence(word.example_sentence, word.english_word)
                }}
              />
              {showTranslation && (
                <motion.p 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-sm text-gray-500 mt-2 italic"
                >
                  {word.example_sentence_turkish}
                </motion.p>
              )}
            </div>

            {/* Zorluk Seviyesi Noktaları */}
            <div className="flex justify-center mt-6">
              {[1, 2, 3, 4, 5].map((level) => (
                <div
                  key={level}
                  className={`w-3 h-3 rounded-full mx-1 ${
                    level <= word.difficulty ? 'bg-primary' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>

            {/* Tıklama İpucu */}
            {!showTranslation && (
              <p className="text-sm text-gray-300 mt-3">Çevirileri görmek için karta tıklayın</p>
            )}
          </div>
        </motion.div>

        {/* Action Buttons - Kartın hemen altında (1px) */}
        <div className="absolute left-1/2 -translate-x-1/2 top-[calc(100%+8px)] w-full flex justify-center items-center space-x-8 z-20 pointer-events-auto">
          <button
            onClick={onSwipeLeft}
            disabled={isProcessing}
            className={`w-16 h-16 bg-white rounded-full shadow-lg flex items-center justify-center transition-colors border-2 ${
              isProcessing 
                ? 'opacity-50 cursor-not-allowed border-gray-300' 
                : 'hover:bg-red-50 border-red-200 hover:border-red-400'
            }`}
          >
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <button
            onClick={onSwipeRight}
            disabled={isProcessing}
            className={`w-16 h-16 bg-white rounded-full shadow-lg flex items-center justify-center transition-colors border-2 ${
              isProcessing 
                ? 'opacity-50 cursor-not-allowed border-gray-300' 
                : 'hover:bg-green-50 border-green-200 hover:border-green-400'
            }`}
          >
            <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default WordCard;
